import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions } from 'components/Assessments/adapters/types'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'

/**
 * Supervisory Review subsection adapter for SOC Assessment
 * Handles supervisory review extraction and transformation
 */
export class SupervisoryReviewAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const idBadge = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE
    ).at(0)

    const honoringRights = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS
    ).at(0)

    const emergencyProtocol = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL
    ).at(0)

    const hygienePrecautions = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS
    ).at(0)

    const reportingChanges = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES
    ).at(0)

    const grievanceComplaint = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT
    ).at(0)

    const pocDocReviewedWithCaregiver = getAnswerValuesFromItems(
      [
        flatAnswers[
          SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER
        ]
      ],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER
    ).at(0)

    const ppeAccess = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS
    ).at(0)

    const supervisionObserved = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED
    ).at(0)

    const taskObserved = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED
    ).at(0)

    const patientStatus = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS
    ).at(0)

    const comments = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS]],
      SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS
    ).at(0)

    return {
      [SocFormField.ID_BADGE]: idBadge,
      [SocFormField.HONORING_RIGHTS]: honoringRights,
      [SocFormField.EMERGENCY_PROTOCOL]: emergencyProtocol,
      [SocFormField.HYGIENE_PRECAUTIONS]: hygienePrecautions,
      [SocFormField.REPORTING_CHANGES]: reportingChanges,
      [SocFormField.GRIEVANCE_COMPLAINT]: grievanceComplaint,
      [SocFormField.POC_DOC_REVIEWED_WITH_CAREGIVER]: pocDocReviewedWithCaregiver,
      [SocFormField.PPE_ACCESS]: ppeAccess,
      [SocFormField.SUPERVISION_OBSERVED]: supervisionObserved,
      [SocFormField.TASK_OBSERVED]: taskObserved,
      [SocFormField.PATIENT_STATUS]: patientStatus,
      [SocFormField.COMMENTS]: comments
    }
  }

  buildFlatAnswers(): Array<{ linkId: string; value: unknown }> {
    return [
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE,
        value: this.getFormDataField(SocFormField.ID_BADGE)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS,
        value: this.getFormDataField(SocFormField.HONORING_RIGHTS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL,
        value: this.getFormDataField(SocFormField.EMERGENCY_PROTOCOL)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS,
        value: this.getFormDataField(SocFormField.HYGIENE_PRECAUTIONS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES,
        value: this.getFormDataField(SocFormField.REPORTING_CHANGES)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT,
        value: this.getFormDataField(SocFormField.GRIEVANCE_COMPLAINT)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER,
        value: this.getFormDataField(SocFormField.POC_DOC_REVIEWED_WITH_CAREGIVER)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS,
        value: this.getFormDataField(SocFormField.PPE_ACCESS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED,
        value: this.getFormDataField(SocFormField.SUPERVISION_OBSERVED)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED,
        value: this.getFormDataField(SocFormField.TASK_OBSERVED)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS,
        value: this.getFormDataField(SocFormField.PATIENT_STATUS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS,
        value: this.getFormDataField(SocFormField.COMMENTS)
      }
    ]
  }
}
